import { Check, Close, IconError, Info, Warning } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { type ComponentProps, forwardRef, type ReactNode } from "react";

export type AlertVariant = "success" | "info" | "warning" | "error";

export interface AlertProps extends Omit<ComponentProps<"div">, "role" | "title"> {
	variant: AlertVariant;
	title?: ReactNode;
	description?: ReactNode;
	icon?: boolean;
	background?: boolean;
	closable?: boolean;
	onClose?: () => void;
	closeLabel?: string;
	role?: "alert" | "status";
}

const ICON: Record<AlertVariant, typeof Check> = {
	success: Check,
	info: Info,
	warning: Warning,
	error: IconError,
};

const VARIANT_CLASSES: Record<AlertVariant, { text: string; bg: string; border: string }> = {
	success: {
		text: "text-status-success-text",
		bg: "bg-status-success-bg",
		border: "border-status-success-surface",
	},
	info: {
		text: "text-status-information-text",
		bg: "bg-status-information-bg",
		border: "border-status-information-surface",
	},
	warning: {
		text: "text-status-warning-text",
		bg: "bg-status-warning-bg",
		border: "border-status-warning-surface",
	},
	error: {
		text: "text-status-error-text",
		bg: "bg-status-error-bg",
		border: "border-status-error-surface",
	},
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
	(
		{
			variant,
			title,
			description,
			icon = true,
			background = true,
			closable = false,
			onClose,
			closeLabel = "Close",
			className,
			role,
			...rest
		},
		ref,
	) => {
		const Icon = ICON[variant];
		const tones = VARIANT_CLASSES[variant];
		const resolvedRole =
			role ?? (variant === "warning" || variant === "error" ? "alert" : "status");

		return (
			<div
				ref={ref}
				role={resolvedRole}
				className={clsx(
					"flex items-start gap-2 rounded-[2px]",
					background && [tones.bg, "border border-solid", tones.border, "px-[9px] py-[13px]"],
					tones.text,
					className,
				)}
				{...rest}
			>
				{icon && (
					<span aria-hidden="true" className="shrink-0 mt-[2px]">
						<Icon className="size-3" />
					</span>
				)}
				<div className="flex-1 min-w-0 flex flex-col gap-1">
					{title && <p className="text-sm uppercase leading-[1.15]">{title}</p>}
					{description && <p className="text-xs leading-[1.5]">{description}</p>}
				</div>
				{closable && (
					<button
						type="button"
						aria-label={closeLabel}
						onClick={onClose}
						className="shrink-0 inline-flex items-center justify-center size-5 cursor-pointer"
					>
						<Close className="size-[14px]" />
					</button>
				)}
			</div>
		);
	},
);

Alert.displayName = "Alert";
